import { abbreviateChartValue, chartTicks, formatChartValue } from '../chart-svg';
import { PRINT_COLORS } from '../snapshots';
import { FIGURE_WIDTH, figureTitle, footLines, n1, svgFrame, svgLabel, textWidth, wrapText } from './svg';

// Before / after bars with a multiplier: for each pair (an organization's revenue in 2019 and 2024), two horizontal
// bars on one scale and the ratio printed beside them ("×5.8"). The ratio is computed here, never typed. Pure and
// deterministic.

export const BEFORE_AFTER_MAX_PAIRS = 12;

export interface BeforeAfterPair {
  label: string;
  before: number;
  after: number;
}

export interface BeforeAfterSvgInput {
  title: string;
  pairs: readonly BeforeAfterPair[];
  /** The two periods ("2019", "2024"). */
  beforeLabel: string;
  afterLabel: string;
  /** ISO 4217 for amounts; null for counts. */
  currency?: string | null;
  source?: string | null;
}

const PAD = 20;
const TOP = 44;
const BAR_H = 12;
const PAIR_H = 44;

/** "×5.8", "×0.4"; "—" when the first value is 0. One decimal under 10, none above. */
export function multiplier(before: number, after: number): string {
  if (!(before > 0)) return '—';
  const r = after / before;
  return `×${r >= 10 ? Math.round(r) : Math.round(r * 10) / 10}`;
}

export function beforeAfterSvg(input: BeforeAfterSvgInput): { svg: string; width: number; height: number } {
  const width = FIGURE_WIDTH;
  const pairs = input.pairs.slice(0, BEFORE_AFTER_MAX_PAIRS);
  const currency = input.currency ?? null;
  if (!pairs.length) {
    const h = TOP + 40;
    return {
      svg: svgFrame(
        width,
        h,
        input.title,
        figureTitle(input.title) + svgLabel('Nothing to compare.', PAD, TOP + 16, { fill: PRINT_COLORS.muted }),
      ),
      width,
      height: h,
    };
  }
  const max = Math.max(...pairs.flatMap(p => [p.before, p.after]), 0);
  const ticks = chartTicks(max, 4, !currency);
  const axisMax = ticks[ticks.length - 1] as number;
  const short = (v: number) => abbreviateChartValue(v, currency);
  const full = (v: number) => formatChartValue(v, currency);
  const labelW = Math.min(220, Math.max(80, Math.max(...pairs.map(p => textWidth(p.label))) + 8));
  const x0 = PAD + labelW + 10;
  const multW = 56;
  const valueW = Math.max(...pairs.flatMap(p => [textWidth(short(p.before), 10), textWidth(short(p.after), 10)])) + 10;
  const plotW = width - PAD - multW - valueW - x0;
  const x = (v: number) => x0 + (Math.max(0, v) / (axisMax || 1)) * plotW;
  const legendY = TOP;
  const plotTop = legendY + 24;
  const parts: string[] = [
    figureTitle(input.title),
    `<rect x="${x0}" y="${legendY - 5}" width="10" height="10" rx="2" fill="${PRINT_COLORS.line}"/>`,
    svgLabel(input.beforeLabel, x0 + 14, legendY, { size: 10, max: 24 }),
    `<rect x="${x0 + 110}" y="${legendY - 5}" width="10" height="10" rx="2" fill="${PRINT_COLORS.entity(1)}"/>`,
    svgLabel(input.afterLabel, x0 + 124, legendY, { size: 10, max: 24 }),
  ];
  const plotBottom = plotTop + pairs.length * PAIR_H;
  for (const t of ticks)
    parts.push(
      `<line x1="${n1(x(t))}" y1="${plotTop - 4}" x2="${n1(x(t))}" y2="${plotBottom}" stroke="${PRINT_COLORS.grid}" stroke-width="1"/>`,
      svgLabel(short(t), x(t), plotBottom + 12, { size: 10, fill: PRINT_COLORS.muted, align: 'center', max: 14 }),
    );
  pairs.forEach((p, i) => {
    const top = plotTop + i * PAIR_H + 4;
    parts.push(
      svgLabel(p.label, x0 - 10, top + PAIR_H / 2 - 4, { size: 11, max: 34, align: 'left' }),
      `<rect x="${n1(x0)}" y="${n1(top)}" width="${n1(x(p.before) - x0)}" height="${BAR_H}" rx="2" fill="${PRINT_COLORS.line}"/>`,
      svgLabel(short(p.before), x(p.before) + 6, top + BAR_H / 2, { size: 10, max: 16 }),
      `<rect x="${n1(x0)}" y="${n1(top + BAR_H + 4)}" width="${n1(x(p.after) - x0)}" height="${BAR_H}" rx="2" fill="${PRINT_COLORS.entity(1)}"/>`,
      svgLabel(short(p.after), x(p.after) + 6, top + BAR_H + 4 + BAR_H / 2, { size: 10, max: 16 }),
      svgLabel(multiplier(p.before, p.after), width - PAD, top + PAIR_H / 2 - 4, { size: 13, weight: 700, align: 'left', max: 8 }),
    );
  });
  parts.push(`<line x1="${n1(x0)}" y1="${plotTop - 4}" x2="${n1(x0)}" y2="${plotBottom}" stroke="${PRINT_COLORS.line}" stroke-width="1"/>`);
  const foot = input.source ? footLines(wrapText(input.source, 135, 2), plotBottom + 32) : null;
  const height = Math.round(plotBottom + 24 + (foot ? foot.height + 12 : 0));
  if (foot) parts.push(foot.svg);
  const desc = pairs.map(p => `${p.label}: ${full(p.before)} → ${full(p.after)} (${multiplier(p.before, p.after)})`).join('; ');
  return {
    svg: svgFrame(width, height, `${input.title}. ${input.beforeLabel} vs ${input.afterLabel}. ${desc}`, parts.join('\n')),
    width,
    height,
  };
}

/** The pairs as a table: label, before, after, multiplier (computed). */
export function beforeAfterTable(
  input: Pick<BeforeAfterSvgInput, 'pairs' | 'beforeLabel' | 'afterLabel' | 'currency'>,
  first: string,
  ratio: string,
): { headers: string[]; rows: string[][] } {
  const full = (v: number) => formatChartValue(v, input.currency ?? null);
  return {
    headers: [first, input.beforeLabel, input.afterLabel, ratio],
    rows: input.pairs.slice(0, BEFORE_AFTER_MAX_PAIRS).map(p => [p.label, full(p.before), full(p.after), multiplier(p.before, p.after)]),
  };
}
